import { HOME_DELIVERY, LOCAL_PICKUP } from '../constants/deliveryTypes'
import { shippingAddressObject } from './addressParser'
import { getTitleDiscountCodes } from './utils'

const selectWeighted = (weights) => {
  const activeEntries = Object.entries(weights).filter(([, weight]) => weight > 0)
  if (!activeEntries.length) return null

  const total = activeEntries.reduce((sum, [, weight]) => sum + weight, 0)
  const random = Math.random() * total
  let accumulated = 0
  for (const [key, weight] of activeEntries) {
    accumulated += weight
    if (random < accumulated) return key
  }
  return activeEntries[activeEntries.length - 1][0]
}

const LEGACY_SOCKET_SERVERS = {
  'pinflag-sa:socket': 'pinflag-sa'
}

const FALLBACK_ASSIGNMENT = { region: 'us-east-1', scraper: 'socket', isLegacyServer: false }

export const CONTAINER_REGION = 'pinflag-container'

export const CONTAINER_SCRAPER_VERSION = 'container'

const CONTAINER_ASSIGNMENT = { region: CONTAINER_REGION, scraper: 'socket', isLegacyServer: false }

const CONTAINER_START_REJECTIONS = ['busy', 'draining']

export const isContainerStartRejection = (closeEvent) => CONTAINER_START_REJECTIONS.includes(closeEvent?.reason)

const SCRAPER_TIME_LIMITS = {
  container: { sessionMs: 280000, paymentStepMs: 260000 },
  lambda: { sessionMs: 240000, paymentStepMs: 220000 }
}

export const getScraperTimeLimits = (isContainer) =>
  isContainer ? SCRAPER_TIME_LIMITS.container : SCRAPER_TIME_LIMITS.lambda

export const computeScraperAssignment = (scraperDistribution, usesContainerScraper = false) => {
  if (usesContainerScraper) return { ...CONTAINER_ASSIGNMENT }

  if (!scraperDistribution) return FALLBACK_ASSIGNMENT

  const selected = selectWeighted(scraperDistribution)
  if (!selected) return FALLBACK_ASSIGNMENT

  const legacyServer = LEGACY_SOCKET_SERVERS[selected]
  if (legacyServer) return { server: legacyServer, scraper: 'socket', isLegacyServer: true }

  const [region, scraper] = selected.split(':')
  if (!region || !scraper) return FALLBACK_ASSIGNMENT

  return { region, scraper, isLegacyServer: false }
}

export const resolveScraperVersion = (scraperAssignment, didFallbackFromContainer) => {
  if (scraperAssignment?.region === CONTAINER_REGION) return CONTAINER_SCRAPER_VERSION
  if (didFallbackFromContainer) {
    return `${CONTAINER_SCRAPER_VERSION}>${scraperAssignment?.scraper ?? 'unknown'}`
  }
  return scraperAssignment?.scraper
}

export const waitForProxyScraper = (scraperAssignment) => {
  if (scraperAssignment?.scraper !== 'proxy') return Promise.resolve()

  return new Promise(resolve => setTimeout(resolve, 3000))
}

export const buildMessageShipping = async ({
  wentBackwards = false,
  userInfo,
  addressInfo,
  configurations,
  isUserValidated,
  companyInfo,
  getConfigurationsShopify,
  getLocation
}) => {
  let locationName
  const { serviceName: shippingRate, deliveryType, cmsId, addressIndex } = addressInfo

  if (userInfo.isRegistered && !isUserValidated && deliveryType === HOME_DELIVERY) {
    if (wentBackwards) return { shippingRate, addressIndex, encryptedData: userInfo.encryptedData, deliveryType }
    return { shippingRate, addressIndex, deliveryType }
  }

  if (deliveryType === LOCAL_PICKUP && cmsId) {
    const location = await getLocation(cmsId)
    locationName = location.data.name
  }

  let storeConfig = configurations
  if (!Object.keys(configurations)?.length) storeConfig = getConfigurationsShopify()

  const addressObject = shippingAddressObject(
    userInfo,
    addressInfo,
    storeConfig
  )
  const { email } = userInfo

  if (companyInfo.companyName === 'Tost') {
    if (!addressObject.phone.startsWith('+56')) {
      addressObject.phone = '+56' + addressObject.phone
    }
  }

  const { address1, address2, city, company, province, firstName, lastName, zip, phone } = addressObject

  if (userInfo.isRegistered && !isUserValidated && deliveryType !== HOME_DELIVERY) {
    if (wentBackwards) return { countryCode: 'CL', address1, address2, city, company, province, shippingRate, deliveryType, locationName, encryptedData: userInfo.encryptedData }
    return { countryCode: 'CL', address1, address2, city, company, province, shippingRate, deliveryType, locationName }
  }

  return {
    countryCode: 'CL',
    address1,
    address2,
    city,
    company,
    province,
    first_name: firstName,
    last_name: lastName,
    zip,
    phone,
    email,
    shippingRate,
    deliveryType,
    locationName,
    requireShipping: addressInfo.requireShipping
  }
}

export const buildCustomerData = async ({
  userInfo,
  discountCodes,
  addressInfo,
  configurations,
  isUserValidated,
  companyInfo,
  getConfigurationsShopify,
  createScrapperURL,
  getLocation,
  selectedPaymentMethod = null
}) => {
  const shipping = await buildMessageShipping({ userInfo, addressInfo, configurations, isUserValidated, companyInfo, getConfigurationsShopify, getLocation })

  return {
    url: createScrapperURL(),
    discountCodes: getTitleDiscountCodes(discountCodes),
    selectedPaymentMethod,
    shippingRate: shipping.shippingRate,
    completeData: shipping,
    encryptedData: userInfo.encryptedData
  }
}
